import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Download, Loader2 } from "lucide-react";
import {
  useRequestNCDHHSExport,
  useRequestMeridianILExport,
  useRequestNjRhtExport,
  useRequestAllMarketsExport,
} from "@/lib/api/exports";

interface RequestExportDialogProps {
  title: string;
  description: string;
  items: string[];
  useMutation: typeof useRequestNCDHHSExport;
}

function RequestExportDialogBase({
  title,
  description,
  items,
  useMutation,
}: RequestExportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: requestExport, isPending } = useMutation();

  const handleConfirm = async () => {
    try {
      await requestExport();
      setIsOpen(false);
    } catch (error) {
      console.error("Export request failed:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Download className="h-4 w-4" />
          {title}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                What&apos;s included in this export:
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc space-y-1 pl-5">
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting Export...
              </>
            ) : (
              "Confirm Export"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RequestNCDHHSExportDialog() {
  return (
    <RequestExportDialogBase
      title="Export NCDHHS Report"
      description="This will generate a comprehensive NCDHHS data export. The process may take several minutes to complete. You will be notified when the export is ready for download."
      items={[
        "Teen demographic data",
        "Mentorship engagement metrics",
        "Communication statistics",
        "Survey responses and assessments",
      ]}
      useMutation={useRequestNCDHHSExport}
    />
  );
}

export function RequestMeridianILExportDialog() {
  return (
    <RequestExportDialogBase
      title="Export IL Meridian Report"
      description="This will generate a comprehensive IL Meridian data export. The process may take several minutes to complete. You will be notified when the export is ready for download."
      items={[
        "Teen demographic data",
        "Mentorship engagement metrics",
        "Communication statistics",
        "Meridian eligibility matching",
      ]}
      useMutation={useRequestMeridianILExport}
    />
  );
}

export function RequestNjRhtExportDialog() {
  return (
    <RequestExportDialogBase
      title="Export NJ RHT Report"
      description="This will generate a comprehensive NJ RHT data export. The process may take several minutes to complete. You will be notified when the export is ready for download."
      items={[
        "Teen demographic data",
        "Mentorship engagement metrics",
        "Communication statistics",
        "Survey responses and assessments",
      ]}
      useMutation={useRequestNjRhtExport}
    />
  );
}

export function RequestAllMarketsExportDialog() {
  return (
    <RequestExportDialogBase
      title="Export All Markets Report"
      description="This will generate a combined data export across all markets. The process may take several minutes to complete. You will be notified when the export is ready for download."
      items={[
        "All teens across every market",
        "All-time engagement data per teen",
        "Survey responses and assessments",
        "County FIPS, HPSA, and NCHS enrichment",
      ]}
      useMutation={useRequestAllMarketsExport}
    />
  );
}
